import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ListButton } from "@refinedev/antd";
import { useGetIdentity } from "@refinedev/core";
import {
  Alert,
  Card,
  Flex,
  Result,
  Spin,
  Switch,
  Table,
  Typography,
  message,
} from "antd";
import { LeftOutlined, ScissorOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utilities";
import { PaginationTotal } from "@/components/pagination-total";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";
import {
  StoreOptimizerConfig,
  getStoreOptimizerConfig,
  updateStoreOptimizerConfig,
} from "../../sellers/optimizer/api";

const { Text } = Typography;

/**
 * Apps → Optimizer del store (xcons-docs#524): si el store ofrece el optimizador de cortes. Qué
 * máquina corta y cómo cobra lo decide cada seller en su propio Apps → Optimizer.
 *
 * El nivel sale de la sesión (`store_optimizer_config`), que es Sí/No y arranca en Sin acceso.
 * El backend rechaza igual: esto es sólo para no ofrecer un switch que va a dar 403.
 */
const puedeCambiar = (identity: any, storeCode: string): boolean => {
  const nivel = String(
    identity?.permissions?.store?.store_optimizer_config?.[storeCode] ??
      "no_access",
  )
    .trim()
    .toLowerCase();
  return nivel === "admin";
};

/** El borde de las tablas del admin, el mismo de Canales. */
const TABLA_CON_BORDE: React.CSSProperties = {
  border: "1px solid #d9d9d9",
  borderRadius: 8,
  overflow: "hidden",
  backgroundColor: "#fff",
};

export const StoreOptimizerConfigList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const storeCode = decodeURIComponent(id ?? "");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<any>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<"forbidden" | "error" | null>(
    null,
  );
  const [config, setConfig] = useState<StoreOptimizerConfig | null>(null);

  useEffect(() => {
    if (!storeCode) return;
    setLoading(true);
    getStoreOptimizerConfig(storeCode)
      .then(setConfig)
      .catch((e: any) => {
        const forbidden = e?.response?.status === 403;
        setLoadError(forbidden ? "forbidden" : "error");
        message.error(
          forbidden
            ? "No tenés permisos para ver el optimizador de este store"
            : "No se pudo cargar la configuración del optimizador",
        );
      })
      .finally(() => setLoading(false));
  }, [storeCode]);

  const toggle = async (offer: boolean) => {
    setSaving(true);
    try {
      setConfig(await updateStoreOptimizerConfig(storeCode, offer));
      message.success(
        offer
          ? "El store ofrece el optimizador"
          : "El store dejó de ofrecer el optimizador",
      );
    } catch (e: any) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const editable = puedeCambiar(identity, storeCode);
  const conCuenta =
    config?.sellers.filter((s) => s.hasLeptonAccount).length ?? 0;

  // Hasta tener la config, sólo el spinner: con "Volver" solo parecía que algo había fallado
  // (xcons-docs#552). Mismo spinner que el Catálogo.
  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Flex vertical style={{ height: "100%", minHeight: 0 }}>
      <Flex align="center" gap={8} style={{ marginBottom: 24, flexShrink: 0 }}>
        <ScissorOutlined style={{ fontSize: 22 }} />
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t("optimizer.store.title", "Optimizador de cortes")}
        </Typography.Title>
      </Flex>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {loadError ? (
          <Result
            status={loadError === "forbidden" ? "403" : "error"}
            title={
              loadError === "forbidden"
                ? "No tenés permisos para ver esta configuración"
                : "No se pudo cargar la configuración"
            }
            subTitle={
              loadError === "forbidden"
                ? "Pedile a un administrador del store el permiso de Optimizer."
                : "Volvé a intentar en un momento."
            }
          />
        ) : (
          config && (
            <Flex vertical gap={16}>
              <Card>
                <Flex justify="space-between" align="center" gap={16}>
                  <Flex vertical>
                    <Text strong>Ofrecer optimizaciones de tableros</Text>
                    <Text type="secondary">
                      La ficha de un tablero muestra el optimizador si esto está
                      prendido y el seller del tablero tiene su cuenta de Lepton
                      cargada.
                    </Text>
                  </Flex>
                  <Switch
                    checked={config.offerBoardOptimizations}
                    loading={saving}
                    disabled={!editable}
                    onChange={toggle}
                  />
                </Flex>
              </Card>

              {config.offerBoardOptimizations && conCuenta === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message="Ningún seller de este store tiene cuenta de Lepton"
                  description="El optimizador no va a aparecer en ninguna ficha hasta que un seller cargue la suya en su Apps → Optimizer."
                />
              )}

              {/* Una tabla como las demás del admin, con una sola fila de encabezados: el título va en
                  la primera columna (xcons-docs#552). Borde y filas finas, como en Canales. */}
              <div style={TABLA_CON_BORDE}>
                <Table
                  className="tabla-en-contenedor"
                  rowKey="sellerCode"
                  size="small"
                  dataSource={config.sellers}
                  pagination={{
                    defaultPageSize: FILAS_POR_PAGINA,
                    pageSizeOptions: OPCIONES_POR_PAGINA,
                    showTotal: (total) => (
                      <PaginationTotal total={total} entityName="sellers" />
                    ),
                  }}
                  locale={{
                    emptyText: "Este store no tiene sellers vinculados",
                  }}
                  columns={[
                    {
                      title: "Sellers del store",
                      key: "seller",
                      render: (_, s) => s.name ?? s.sellerCode,
                    },
                    {
                      title: "Ofrece optimización",
                      key: "ofrece",
                      // Ofrece cuando las dos cosas están: el switch del store y la cuenta del seller.
                      render: (_, s) =>
                        config.offerBoardOptimizations && s.hasLeptonAccount
                          ? "Sí"
                          : "No",
                    },
                  ]}
                />
              </div>
            </Flex>
          )
        )}
      </div>

      <div style={{ paddingTop: 32, paddingBottom: 16, flexShrink: 0 }}>
        <ListButton icon={<LeftOutlined />} onClick={() => navigate(-1)}>
          {t("buttons.back", "Volver")}
        </ListButton>
      </div>
    </Flex>
  );
};
